import React, { Component } from "react";
import { can } from "../utils/permission";
import {
  company,
  loading,
  alert,
  GET,
  POST,
  PUT,
  DELETE,
  t,
} from "./CustomComponent.js";

/**
 * ตั้งค่าวันหยุด (แท็บย่อยของ "ตั้งค่าระบบ")
 * - sub-tab "ประเภทวันหยุด": master scope=company/employee แยก list กันเด็ดขาด (แก้ scope ไม่ได้หลังสร้าง)
 * - sub-tab "วันหยุดประจำสัปดาห์": ใช้กับ "ทั้งบริษัท" เท่านั้น ไม่มีตัวเลือกรายช่าง
 * API: admin/holiday-types, admin/holidays (scope=company&kind=weekly), admin/holidays/weekly
 */

const TYPES_ENDPOINT = "admin/holiday-types";
const WEEKLY_ENDPOINT = "admin/holidays/weekly";
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export default class HolidaySettingsPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      subtab: "types", // 'types' | 'weekly'

      types_company: [],
      types_employee: [],
      types_loaded: false,

      selected_type: null,
      is_type_editing: false,
      type_form_mode: null, // 'create' | 'edit'
      type_form_scope: null, // 'company' | 'employee'
      type_name: "",
      type_is_active: true,

      weekly_loaded: false,
      weekly_enabled: false,
      weekly_days: [],
      weekly_saved_days: [],
    };
  }

  componentDidMount() {
    this.loadTypes();
  }

  loadTypes = async () => {
    loading(true);
    const [resCompany, resEmployee] = await Promise.all([
      GET(TYPES_ENDPOINT, { scope: "company" }),
      GET(TYPES_ENDPOINT, { scope: "employee" }),
    ]);
    this.setState({
      types_company: resCompany?.status ? (resCompany.data || []) : [],
      types_employee: resEmployee?.status ? (resEmployee.data || []) : [],
      types_loaded: true,
    });
    loading(false);
  };

  loadWeekly = async () => {
    loading(true);
    const result = await GET("admin/holidays", { scope: "company", kind: "weekly" });
    const rows = result?.status && Array.isArray(result.data) ? result.data : [];
    const days = rows
      .map((row) => row.weekday)
      .filter((day) => day !== null && day !== undefined);
    this.setState({
      weekly_days: days,
      weekly_saved_days: days,
      weekly_enabled: days.length > 0,
      weekly_loaded: true,
    });
    loading(false);
  };

  handleSubtabChange = (subtab) => {
    this.setState({ subtab }, () => {
      if (subtab === "weekly" && !this.state.weekly_loaded) this.loadWeekly();
    });
  };

  // --- ประเภทวันหยุด (CRUD) ---

  openAddType = (scope) => {
    this.setState({
      selected_type: null,
      is_type_editing: true,
      type_form_mode: "create",
      type_form_scope: scope,
      type_name: "",
      type_is_active: true,
    });
  };

  selectType = (item) => {
    this.setState({
      selected_type: item,
      is_type_editing: true,
      type_form_mode: "edit",
      type_form_scope: item.scope,
      type_name: item.name || "",
      type_is_active: item.is_active !== false,
    });
  };

  cancelTypeForm = () => {
    this.setState({
      selected_type: null,
      is_type_editing: false,
      type_form_mode: null,
      type_form_scope: null,
      type_name: "",
      type_is_active: true,
    });
  };

  saveType = async () => {
    const { type_form_mode, type_form_scope, type_name, type_is_active, selected_type } = this.state;

    if (!type_name.trim()) {
      alert("", "warning", t("warning"), t("please_fill_required"));
      return;
    }

    if (!(await alert("", "info", t("confirm"), t("confirm_save_bom"), t("confirm"), t("cancel")))) return;

    loading(true);
    let result;
    if (type_form_mode === "create") {
      result = await POST(TYPES_ENDPOINT, {
        company_id: company?.id || "",
        name: type_name.trim(),
        scope: type_form_scope,
        is_active: type_is_active,
      });
    } else {
      result = await PUT(`${TYPES_ENDPOINT}/${selected_type?.uid}`, {
        name: type_name.trim(),
        is_active: type_is_active,
      });
    }

    if (result?.status === true) {
      alert("", "success", t("success"), t("save_success"));
      this.cancelTypeForm();
      await this.loadTypes();
    } else {
      alert("", "warning", t("warning"), result?.message || t("error_occurred"));
    }
    loading(false);
  };

  deleteType = async (uid) => {
    if (await alert("", "warning", t("delete_item"), t("delete_desc"), t("confirm"), t("cancel"))) {
      loading(true);
      const result = await DELETE(`${TYPES_ENDPOINT}/${uid}`, null);
      if (result?.status) {
        alert("", "success", t("success"), t("delete_success"));
        this.cancelTypeForm();
        await this.loadTypes();
      } else {
        alert("", "warning", t("warning"), result?.message || t("error_occurred"));
      }
      loading(false);
    }
  };

  // --- วันหยุดประจำสัปดาห์ (ทั้งบริษัทเท่านั้น) ---

  toggleWeekday = (day) => {
    this.setState((prev) => {
      const set = new Set(prev.weekly_days);
      if (set.has(day)) set.delete(day);
      else set.add(day);
      return { weekly_days: [...set] };
    });
  };

  cancelWeekly = () => {
    this.setState((prev) => ({
      weekly_days: prev.weekly_saved_days,
      weekly_enabled: prev.weekly_saved_days.length > 0,
    }));
  };

  saveWeekly = async () => {
    if (!(await alert("", "info", t("confirm"), t("confirm_save_bom"), t("confirm"), t("cancel")))) return;

    loading(true);
    const weekdays = this.state.weekly_enabled ? this.state.weekly_days : [];
    const result = await PUT(WEEKLY_ENDPOINT, {
      company_id: company?.id || "",
      weekdays,
    });

    if (result?.status === true) {
      alert("", "success", t("success"), t("save_success"));
      this.setState({ weekly_saved_days: weekdays, weekly_days: weekdays });
    } else {
      alert("", "warning", t("warning"), result?.message || t("error_occurred"));
    }
    loading(false);
  };

  // --- RENDER: ประเภทวันหยุด ---

  renderTypeItem = (item) => {
    const { selected_type } = this.state;
    const isSelected = Boolean(selected_type) && selected_type.uid === item.uid;
    const dotColor = item.scope === "company" ? "#64748b" : "#e74c3c";

    return (
      <div
        key={item.uid}
        className={`border rounded-3 p-2 d-flex align-items-center justify-content-between pointer transition-hover ${
          isSelected ? "border-primary bg-primary-light shadow-sm" : "border bg-white"
        }`}
        onClick={() => this.selectType(item)}
      >
        <div className="d-flex align-items-center">
          <span
            className="rounded-2 me-2 flex-shrink-0"
            style={{ width: 10, height: 10, backgroundColor: dotColor, display: "inline-block" }}
          ></span>
          <span className="text-14 fw-bold text-dark">{item.name}</span>
          {item.is_active === false && (
            <span className="badge bg-light text-secondary border ms-2 text-10">{t("inactive")}</span>
          )}
        </div>
        <span className="icon text-secondary text-12">{""}</span>
      </div>
    );
  };

  renderTypesLeftPanel = () => {
    const { types_company, types_employee } = this.state;

    return (
      <div className="bg-white rounded-4 shadow-sm p-3 h-100">
        <div className="mb-3 pb-2 border-bottom">
          <h6 className="fw-bold mb-1">{t("hset_subtab_types")}</h6>
          <div className="text-secondary text-10">{t("hset_types_panel_sub")}</div>
        </div>

        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-12 fw-bold text-dark d-flex align-items-center">
              <span className="icon me-2 text-secondary">{""}</span> {t("hset_scope_company_group")}
            </span>
            {can("holiday.create") && (
              <button className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={() => this.openAddType("company")}>
                + {t("add")}
              </button>
            )}
          </div>
          <div className="d-flex flex-column gap-2">
            {types_company.map((item) => this.renderTypeItem(item))}
          </div>
          <div className="text-secondary text-10 mt-2 px-1">
            {t("hset_go_to_weekly_hint")}{" "}
            <span className="text-primary pointer fw-bold" onClick={() => this.handleSubtabChange("weekly")}>
              {t("hset_subtab_weekly")}
            </span>
          </div>
        </div>

        <div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-12 fw-bold text-dark d-flex align-items-center">
              <span className="icon me-2 text-danger">{""}</span> {t("hset_scope_employee_group")}
            </span>
            {can("holiday.create") && (
              <button className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={() => this.openAddType("employee")}>
                + {t("add")}
              </button>
            )}
          </div>
          <div className="d-flex flex-column gap-2">
            {types_employee.map((item) => this.renderTypeItem(item))}
          </div>
        </div>
      </div>
    );
  };

  renderTypeDetail = () => {
    const { is_type_editing, type_form_mode, type_form_scope, type_name, type_is_active, selected_type } = this.state;

    if (!is_type_editing) {
      return (
        <div className="bg-white rounded-4 shadow-sm p-5 h-100 d-flex flex-column align-items-center justify-content-center text-center border">
          <span className="icon text-light text-60 mb-3">{""}</span>
          <div className="text-secondary text-14">{t("hset_type_empty_line1")}</div>
          <div className="text-secondary text-14">{t("hset_type_empty_line2")}</div>
        </div>
      );
    }

    const scopeLabel = type_form_scope === "company" ? t("hd_scope_company") : t("hd_scope_technician");

    return (
      <div className="bg-white rounded-4 shadow-sm h-100 d-flex flex-column">
        <div className="px-4 py-3 border-bottom bg-light rounded-top-4">
          <h5 className="fw-bold mb-1 text-dark d-flex align-items-center">
            <span className="icon me-2 text-primary">{""}</span>
            {type_form_mode === "create" ? t("hset_type_add_title") : t("hset_type_edit_title")}
          </h5>
        </div>

        <div className="p-4 flex-grow-1 overflow-auto">
          <div className="mb-3">
            <label className="form-label text-dark fw-bold text-14">
              {t("name_th")} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control py-2"
              placeholder={t("hset_type_name_ph")}
              value={type_name}
              onChange={(e) => this.setState({ type_name: e.target.value })}
            />
          </div>

          <div className="mb-4">
            <label className="form-label text-dark fw-bold text-14">{t("hset_type_scope_label")}</label>
            <input type="text" className="form-control py-2 bg-light text-secondary" value={scopeLabel} disabled />
            <div className="text-secondary text-10 mt-1">
              {t("hset_type_scope_locked_hint").replace("{scope}", scopeLabel)}
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <span className="text-14 fw-bold text-dark">{t("status_active")}</span>
            <div className="form-check form-switch m-0">
              <input
                className="form-check-input fs-5 pointer"
                type="checkbox"
                checked={type_is_active}
                onChange={(e) => this.setState({ type_is_active: e.target.checked })}
              />
            </div>
          </div>
        </div>

        <div className="p-3 border-top bg-light rounded-bottom-4 d-flex justify-content-between align-items-center gap-2">
          {type_form_mode === "edit" && can("holiday.delete") ? (
            <button
              className="btn px-4 py-2 rounded-3 fw-bold"
              style={{ backgroundColor: "#fdecec", color: "#c0392b" }}
              onClick={() => this.deleteType(selected_type?.uid)}
            >
              <span className="icon me-2">{""}</span> {t("delete_item")}
            </button>
          ) : (
            <span />
          )}
          <div className="d-flex gap-2">
            <button className="btn btn-white border px-4 py-2 text-dark rounded-3 fw-bold" onClick={this.cancelTypeForm}>
              {t("cancel")}
            </button>
            {(type_form_mode === "create" ? can("holiday.create") : can("holiday.update")) && (
              <button
                className="btn btn-primary px-4 py-2 d-flex align-items-center rounded-3 fw-bold text-white shadow-sm"
                style={{ backgroundColor: "#2563eb", borderColor: "#2563eb" }}
                onClick={this.saveType}
              >
                <span className="icon me-2">{""}</span> {t("save")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // --- RENDER: วันหยุดประจำสัปดาห์ ---

  renderWeeklySubtab = () => {
    const { weekly_loaded, weekly_enabled, weekly_days } = this.state;
    if (!weekly_loaded) return null;

    return (
      <div className="bg-white rounded-4 shadow-sm p-4" style={{ maxWidth: 640 }}>
        <h6 className="fw-bold mb-1 text-dark">{t("hset_subtab_weekly")}</h6>
        <div className="text-secondary text-12 mb-3">{t("hset_weekly_only_company_hint")}</div>

        <div className="bg-primary-light border border-primary rounded-3 p-2 mb-3 text-12 text-primary d-flex align-items-start">
          <span className="icon me-2">{""}</span> {t("hset_weekly_warn")}
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="text-14 fw-bold text-dark">{t("hset_weekly_enable")}</span>
          <div className="form-check form-switch m-0">
            <input
              className="form-check-input fs-5 pointer"
              type="checkbox"
              checked={weekly_enabled}
              onChange={(e) => this.setState({ weekly_enabled: e.target.checked })}
            />
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-4">
          {WEEKDAY_KEYS.map((key, day) => {
            const isOn = weekly_enabled && weekly_days.includes(day);
            return (
              <div
                key={key}
                className={`d-flex align-items-center justify-content-center rounded-3 border fw-bold text-12 ${weekly_enabled ? "pointer" : ""}`}
                style={{
                  width: 56,
                  height: 56,
                  backgroundColor: isOn ? "#2563eb" : "#fff",
                  color: isOn ? "#fff" : "#555",
                  borderColor: isOn ? "#2563eb" : "#e5e7eb",
                  opacity: weekly_enabled ? 1 : 0.5,
                }}
                onClick={() => (weekly_enabled ? this.toggleWeekday(day) : null)}
              >
                {t(key)}
              </div>
            );
          })}
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-white border px-4 py-2 text-dark rounded-3 fw-bold" onClick={this.cancelWeekly}>
            {t("cancel")}
          </button>
          {can("holiday.update") && (
            <button
              className="btn btn-primary px-4 py-2 d-flex align-items-center rounded-3 fw-bold text-white shadow-sm"
              style={{ backgroundColor: "#2563eb", borderColor: "#2563eb" }}
              onClick={this.saveWeekly}
            >
              <span className="icon me-2">{""}</span> {t("save")}
            </button>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { subtab } = this.state;

    return (
      <div>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <button
            className={`btn btn-sm rounded-pill fw-bold px-3 py-2 ${subtab === "types" ? "btn-primary shadow-sm" : "btn-white text-secondary border"}`}
            onClick={() => this.handleSubtabChange("types")}
          >
            {t("hset_subtab_types")}
          </button>
          <button
            className={`btn btn-sm rounded-pill fw-bold px-3 py-2 ${subtab === "weekly" ? "btn-primary shadow-sm" : "btn-white text-secondary border"}`}
            onClick={() => this.handleSubtabChange("weekly")}
          >
            {t("hset_subtab_weekly")}
          </button>
        </div>

        {subtab === "types" ? (
          <div className="row g-4">
            <div className="col-lg-5 col-xl-4">{this.renderTypesLeftPanel()}</div>
            <div className="col-lg-7 col-xl-8">{this.renderTypeDetail()}</div>
          </div>
        ) : (
          this.renderWeeklySubtab()
        )}
      </div>
    );
  }
}
